import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api/client';
import type { CreateUserRequest, UpdateUserRequest } from '@/api/users';
import type { PermissionCatalog, User } from '@/api/types';

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<User[]>('/api/users'),
  });
}

/** Labels the permission editor renders from — same catalog the server enforces against. */
export function usePermissionCatalog() {
  return useQuery({
    queryKey: ['users', 'permission-catalog'],
    queryFn: () => api.get<PermissionCatalog>('/api/users/permission-catalog'),
    staleTime: Infinity,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateUserRequest) => api.post<User>('/api/users', request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: UpdateUserRequest }) =>
      api.put<User>(`/api/users/${id}`, request),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/users/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}
